/**
 * RoofDetailsQuickBar — top-of-builder live-edit panel.
 *
 * Sits to the right of the Builder sub-tab row (Re-roof / Repairs /
 * Restoration / Maintenance / Guttering) and lets the operator:
 *
 *   • Pick which Roof Details record the bar operates on (when an
 *     appraisal has multiple roofs — e.g. House + Garage).
 *   • Edit the most-used fields (Roof Type / Profile / Roof m² / Brand
 *     / Colour by default) without leaving the builder.
 *   • Customise which fields are visible via the ⚙ Fields modal
 *     (preference persists in localStorage).
 *   • Operate in PREVIEW mode — edits update local state only; the
 *     operator clicks Apply to persist + reload builder, or Discard
 *     to revert.
 *   • Pop the full RoofForm editor in a modal via the ✎ Edit full
 *     button.
 *
 * Persistence on Apply:
 *   1. PUT /roof-details/{_id} with the merged record so all field
 *      changes save in one round-trip.
 *   2. PATCH /appraisals/{id}/inline-field for roof_subtype +
 *      tile_brand_override + tile_profile_override so the active
 *      Restoration builder picks up the new subtype on reload.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Settings2, Edit3, Eye, Check, X, Home, AlertCircle } from 'lucide-react';
import RoofForm from '@/components/RoofForm';

const VISIBLE_PREF_KEY = 'rtr.builder.roof_quickbar.visible_fields.v1';
const DEFAULT_VISIBLE = ['roof_type', 'roof_profile', 'material_type', 'roof_m2', 'roof_brand', 'roof_colour'];

// Full field catalogue — each entry tells the bar how to render the
// inline editor (kind = select | number | text) + a human label and
// (for selects) the dropdown source key on the /dropdowns response.
const FIELD_CATALOGUE = [
  { key: 'description',         label: 'Description',  kind: 'select', src: 'roof_descriptions' },
  { key: 'roof_type',           label: 'Roof Type',    kind: 'select', src: 'roof_types' },
  { key: 'roof_brand',          label: 'Brand',        kind: 'select', src: 'roof_brands', cascadeFrom: 'roof_type' },
  { key: 'roof_profile',        label: 'Profile',      kind: 'select', src: 'roof_profiles', cascadeFrom: 'roof_brand' },
  // Iter146 — Material Type (4th cascade tier for Iron roofs). Drives
  // the Iron-section narrowing on the Components tab (Sheet / Ridge /
  // Valley / Barge / Flashing pre-select rows matching the picked
  // material). `ironOnly: true` tells the renderer to hide this field
  // unless the roof's `roof_type` is Iron.
  { key: 'material_type',       label: 'Material Type',kind: 'select', src: 'material_types', ironOnly: true },
  { key: 'roof_colour',         label: 'Colour',       kind: 'select', src: 'roof_colours' },
  { key: 'roof_pitch',          label: 'Pitch',        kind: 'select', src: 'roof_pitches' },
  { key: 'roof_state',          label: 'State',        kind: 'select', src: 'roof_states' },
  { key: 'colour_condition',    label: 'Surface',      kind: 'select', src: 'colour_conditions' },
  { key: 'organic_growth',      label: 'Growth',       kind: 'select', src: 'organic_growths' },
  { key: 'roof_age',            label: 'Age',          kind: 'select', src: 'roof_ages' },
  { key: 'roof_m2',             label: 'Roof m²',      kind: 'number', step: 'any', unit: 'm²' },
  { key: 'floor_m2',            label: 'Floor m²',     kind: 'number', step: 'any', unit: 'm²' },
  { key: 'roof_angle',          label: 'Angle',        kind: 'number', step: 0.5, unit: '°' },
  { key: 'roof_stories',        label: 'Stories',      kind: 'number', step: 1 },
  { key: 'ridge_length',        label: 'Ridge',        kind: 'number', step: 'any', unit: 'm' },
  { key: 'extra_work_estimate', label: 'Extra work',   kind: 'number', step: 0.01, unit: '$' },
];
const FIELD_MAP = Object.fromEntries(FIELD_CATALOGUE.map(f => [f.key, f]));

// Subtype canonicalisation — Iter 105: the operator-facing Roof Type
// list was shortened to 9 items. The canonical `roof_subtype` value
// stored on the appraisal (and read by the Restoration builder to
// pick the right sub-builder) is derived from BOTH the new short Type
// AND the Brand:
//
//   • Iron + Long Run        → Long-Run        (Iron builder)
//   • Iron + Short Run       → Long-Run        (treated as Long Run per operator)
//   • Iron + <other>         → Long-Run
//   • Concrete + Old Concrete→ Old Concrete    (Concrete builder, Old-rate clone)
//   • Concrete + Modern Conc → Modern Concrete (Concrete builder, Modern clone)
//   • Concrete + <other>     → Modern Concrete (default Concrete clone)
//   • Clay                   → Clay Tile       (Concrete builder, Clay clone w/ Old rates)
//   • Tin Tile + Decramastic → Decramastic
//   • Tin Tile + ColorTile   → Colortile
//   • Tin Tile + <other>     → Tin Tile
//
// Legacy spellings still mapped (Mod Conc → Modern Concrete, Decra →
// Decramastic, etc.) so historic appraisals continue to route correctly.
const LEGACY_TYPE_ALIASES = {
  'mod conc':       'Modern Concrete',
  'old conc':       'Old Concrete',
  'concrete tile':  'Modern Concrete',
  'modern concrete':'Modern Concrete',
  'old concrete':   'Old Concrete',
  'long run iron':  'Long-Run',
  'short run iron': 'Long-Run',
  'long-run':       'Long-Run',
  'longrun':        'Long-Run',
  'iron':           'Long-Run',
  'decramastic':    'Decramastic',
  'decra':          'Decramastic',
  'chipped':        'Decramastic',
  'colortile':      'Colortile',
  'colourtile':     'Colortile',
  'metal tile':     'Tin Tile',
  'tin tile':       'Tin Tile',
  'clay tile':      'Clay',
  'clay':           'Clay',
};

function canonicaliseSubtype(roofType, roofBrand) {
  const rt = (roofType || '').toString().trim();
  const rb = (roofBrand || '').toString().trim();
  if (!rt) return '';
  const rtKey = rt.toLowerCase();
  const rbKey = rb.toLowerCase();
  if (rtKey === 'iron') return 'Long-Run';
  if (rtKey === 'concrete') {
    if (rbKey.includes('old')) return 'Old Concrete';
    return 'Modern Concrete';
  }
  if (rtKey === 'clay' || rtKey === 'clay tile') return 'Clay';
  if (rtKey === 'tin tile') {
    if (rbKey.includes('decra')) return 'Decramastic';
    if (rbKey.includes('color') || rbKey.includes('colour')) return 'Colortile';
    return 'Tin Tile';
  }
  return LEGACY_TYPE_ALIASES[rtKey] || rt;
}

export default function RoofDetailsQuickBar({ api, appraisal, roofDetails, onRoofPreview, onApplied }) {
  const list = Array.isArray(roofDetails) ? roofDetails : [];
  // Operator's chosen roof — persists across re-renders. Resets if the
  // appraisal's underlying roofs change.
  const [selectedId, setSelectedId] = useState(list[0]?._id || '');
  useEffect(() => {
    if (!list.find(r => r._id === selectedId)) {
      setSelectedId(list[0]?._id || '');
    }
  }, [list, selectedId]);
  const selected = list.find(r => r._id === selectedId) || list[0] || null;

  // Visible fields preference — localStorage so the operator keeps
  // their chosen field set across appraisals + sessions.
  const [visibleFields, setVisibleFields] = useState(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_PREF_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.every(k => typeof k === 'string')) return arr;
    } catch (_) { /* ignore */ }
    return DEFAULT_VISIBLE;
  });
  useEffect(() => {
    try { localStorage.setItem(VISIBLE_PREF_KEY, JSON.stringify(visibleFields)); } catch (_) { /* ignore */ }
  }, [visibleFields]);

  // Preview — keyed by roof _id so switching roofs preserves pending
  // edits on the other roof.
  const [previewByRoof, setPreviewByRoof] = useState({});
  const preview = previewByRoof[selectedId] || {};
  const previewCount = Object.keys(preview).length;

  // Dropdown sources — loaded once per mount.
  const [dd, setDd] = useState({});
  useEffect(() => {
    api.get('/dropdowns').then(r => setDd(r.data || {})).catch(() => {});
  }, [api]);

  // Cascade sources — keyed by `${field}::${cascadeValue}` so we don't
  // refetch when the operator clicks the same row twice. The fetcher
  // is referentially stable so the auto-prefetch useEffect below
  // doesn't loop.
  const [cascades, setCascades] = useState({});
  const fetchCascade = useCallback(async (kind, val, roofType = '') => {
    if (!val) return;
    // Iter316 · Phase 1.1 · include roof_type in the cache key for
    // profile cascades so brand-name collisions (Winstone under
    // both Old Concrete & Clay Tile) don't share a single cache
    // entry and cross-leak into the wrong dropdown.
    const key = kind === 'profile' && roofType
      ? `${kind}::${roofType}::${val}`
      : `${kind}::${val}`;
    setCascades(prev => {
      if (prev[key] !== undefined) return prev;
      // Mark as in-flight with empty array so we don't double-fire
      // while the network call is open. Replace on resolve.
      const path = kind === 'brand'
        ? `/roof-brands?roof_type=${encodeURIComponent(val)}`
        : (roofType
            ? `/roof-profiles?brand=${encodeURIComponent(val)}&roof_type=${encodeURIComponent(roofType)}`
            : `/roof-profiles?brand=${encodeURIComponent(val)}`);
      api.get(path)
        .then(({ data }) => setCascades(c => ({ ...c, [key]: Array.isArray(data) ? data : [] })))
        .catch(() => setCascades(c => ({ ...c, [key]: [] })));
      return { ...prev, [key]: [] };
    });
  }, [api]);

  // Modal state
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [editFullOpen, setEditFullOpen] = useState(false);
  const [editRoofDraft, setEditRoofDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Effective value: preview wins over the underlying record.
  const eff = (key) => (key in preview ? preview[key] : (selected?.[key] ?? ''));

  // Pre-fetch cascades for the active roof's Type + Brand so opening
  // the Brand / Profile chip immediately shows the narrowed list —
  // matches the Edit Full RoofForm behaviour where Brand is filtered
  // by Roof Type and Profile is filtered by Brand. Runs on mount +
  // every time the effective parent value changes.
  const effRoofType  = eff('roof_type');
  const effRoofBrand = eff('roof_brand');
  useEffect(() => { if (effRoofType)  fetchCascade('brand',   effRoofType);  }, [effRoofType,  fetchCascade]);
  useEffect(() => { if (effRoofBrand) fetchCascade('profile', effRoofBrand, effRoofType); }, [effRoofBrand, effRoofType, fetchCascade]);

  // Bubble preview overrides up to AppraisalDetailPage so the builders
  // that consume `appraisal.roof_subtype` (Restoration) can hot-swap
  // without persisting.
  useEffect(() => {
    if (!onRoofPreview) return;
    if (!selected || previewCount === 0) {
      onRoofPreview(null);
      return;
    }
    const rt = eff('roof_type');
    const rb = eff('roof_brand');
    const rp = eff('roof_profile');
    onRoofPreview({
      roof_subtype: canonicaliseSubtype(rt, rb),
      tile_brand_override: rb || null,
      tile_profile_override: rp || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, selectedId]);

  // Edit handlers — every chip's inline editor calls patchField(key,v).
  // When the operator changes Roof Type the Brand + Profile no longer
  // belong to the new type's catalogue, so we blank them out at the
  // same time (mirrors RoofForm.onTypeChange / onBrandChange). The
  // blank values flow into preview so the operator sees clear chips
  // and the narrowed dropdown after the cascade resolves.
  const patchField = (key, value) => {
    setPreviewByRoof(p => {
      const cur = { ...(p[selectedId] || {}) };
      const setPreview = (k, v) => {
        const saved = selected?.[k];
        const same = String(saved ?? '') === String(v ?? '');
        if (same) { delete cur[k]; } else { cur[k] = v; }
      };
      setPreview(key, value);
      if (key === 'roof_type')  { setPreview('roof_brand', ''); setPreview('roof_profile', ''); }
      if (key === 'roof_brand') { setPreview('roof_profile', ''); }
      return { ...p, [selectedId]: cur };
    });
    // Pull cascades on the fly so the dependent dropdowns populate.
    if (key === 'roof_type')  fetchCascade('brand', value);
    if (key === 'roof_brand') fetchCascade('profile', value, eff('roof_type'));
  };

  const discardPreview = () => {
    setPreviewByRoof(p => ({ ...p, [selectedId]: {} }));
  };

  const applyPreview = async () => {
    if (!selected || previewCount === 0) return;
    setBusy(true);
    setErr(null);
    try {
      // 1. PUT the merged roof_details record.
      const merged = { ...selected, ...preview };
      const { _id, ...payload } = merged;
      await api.put(`/roof-details/${_id}`, payload);
      // 2. Cascade key fields up onto the appraisal so the builders
      //    pick up the new subtype + tile overrides immediately on
      //    reload.
      const inlineFields = [];
      if ('roof_type' in preview || 'roof_brand' in preview) {
        const rt = preview.roof_type ?? selected.roof_type;
        const rb = preview.roof_brand ?? selected.roof_brand;
        inlineFields.push(['roof_subtype', canonicaliseSubtype(rt, rb)]);
      }
      if ('roof_brand' in preview)   inlineFields.push(['tile_brand_override', preview.roof_brand || null]);
      if ('roof_profile' in preview) inlineFields.push(['tile_profile_override', preview.roof_profile || null]);
      for (const [field, value] of inlineFields) {
        try { await api.patch(`/appraisals/${appraisal._id}/inline-field`, { field, value }); } catch (_) { /* non-fatal */ }
      }
      // 3. Clear the preview + bubble refresh.
      setPreviewByRoof(p => ({ ...p, [selectedId]: {} }));
      if (onApplied) await onApplied();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Apply failed');
    } finally {
      setBusy(false);
    }
  };

  // Source list for a select field (with cascade fallback to flat).
  const optionsFor = (field) => {
    if (field.cascadeFrom) {
      const parent = eff(field.cascadeFrom);
      if (parent) {
        const kind = field.cascadeFrom === 'roof_type' ? 'brand' : 'profile';
        // Iter316 · Phase 1.1 · profile cache is scoped by
        // (roof_type, brand) to avoid Winstone-style collisions.
        const rt = eff('roof_type');
        const k = kind === 'profile' && rt
          ? `${kind}::${rt}::${parent}`
          : `${kind}::${parent}`;
        if (Array.isArray(cascades[k]) && cascades[k].length > 0) return cascades[k];
      }
    }
    return Array.isArray(dd[field.src]) ? dd[field.src] : [];
  };

  if (!selected) {
    return (
      <div data-testid="roof-quickbar-empty"
           className="flex items-center gap-2 px-3 py-2 bg-amber-50/60 border border-amber-200 rounded text-xs text-amber-800">
        <AlertCircle size={13} /> No Roof Details on this appraisal — add one in the Roof Details tab to enable live-edit toggling.
      </div>
    );
  }

  return (
    <>
      <div data-testid="roof-quickbar" className="flex items-stretch gap-1.5 flex-wrap bg-slate-50/80 border border-slate-200 rounded px-2 py-1.5">
        {/* Roof picker — only when the appraisal has >1 roof. */}
        {list.length > 1 && (
          <label className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-slate-500">
            <Home size={11} />
            <select
              data-testid="roof-quickbar-roof-picker"
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="px-1.5 py-0.5 text-xs normal-case font-normal text-slate-700 border border-slate-300 rounded bg-white"
            >
              {list.map(r => (
                <option key={r._id} value={r._id}>
                  {(r.description || 'Roof')} · {r.roof_type || '—'}
                </option>
              ))}
            </select>
          </label>
        )}

        {visibleFields.map(fk => {
          const f = FIELD_MAP[fk];
          if (!f) return null;
          // Iter146 — hide Iron-only fields (Material Type) when the
          // current roof isn't Iron. Keeps the bar compact on tile /
          // concrete inspections.
          if (f.ironOnly && String(eff('roof_type') || '').trim().toLowerCase() !== 'iron') return null;
          const v = eff(fk);
          const isDirty = fk in preview;
          return (
            <FieldChip
              key={fk}
              field={f}
              value={v}
              dirty={isDirty}
              options={f.kind === 'select' ? optionsFor(f) : null}
              onChange={(nv) => patchField(fk, nv)}
            />
          );
        })}
        {/* Iter146 — Material Type lives in the FIELD_CATALOGUE but the
            operator's saved `visible_fields` preference (created before
            iter146 landed) doesn't include it. Force-render the field
            on Iron roofs when it's NOT already in the visible list. */}
        {String(eff('roof_type') || '').trim().toLowerCase() === 'iron' && !visibleFields.includes('material_type') && (
          <FieldChip
            key="material_type-forced"
            field={FIELD_MAP.material_type}
            value={eff('material_type')}
            dirty={'material_type' in preview}
            options={optionsFor(FIELD_MAP.material_type)}
            onChange={(nv) => patchField('material_type', nv)}
          />
        )}

        <button
          type="button"
          data-testid="roof-quickbar-fields-btn"
          onClick={() => setFieldsOpen(true)}
          title="Pick which Roof Details fields show on the builder bar"
          className="px-2 py-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-white rounded border border-dashed border-slate-300 inline-flex items-center gap-1 self-center"
        >
          <Settings2 size={12} /> Fields
        </button>

        <button
          type="button"
          data-testid="roof-quickbar-edit-full-btn"
          onClick={() => { setEditRoofDraft({ ...selected, ...preview }); setEditFullOpen(true); }}
          title="Open the full Roof Details editor in a pop-up"
          className="px-2 py-1 text-[11px] font-semibold text-[#D35400] hover:text-[#A04000] hover:bg-orange-50 rounded border border-[#D35400] inline-flex items-center gap-1 self-center"
        >
          <Edit3 size={12} /> Edit full
        </button>

        {previewCount > 0 && (
          <div data-testid="roof-quickbar-preview-bar" className="ml-auto flex items-center gap-1.5 self-center">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded">
              <Eye size={10} /> Preview · {previewCount} change{previewCount === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              data-testid="roof-quickbar-apply-btn"
              disabled={busy}
              onClick={applyPreview}
              className="px-2.5 py-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded inline-flex items-center gap-1"
            >
              <Check size={11} /> {busy ? 'Saving…' : 'Apply'}
            </button>
            <button
              type="button"
              data-testid="roof-quickbar-discard-btn"
              disabled={busy}
              onClick={discardPreview}
              className="px-2 py-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-white rounded border border-slate-300 inline-flex items-center gap-1"
            >
              <X size={11} /> Discard
            </button>
          </div>
        )}
      </div>

      {err && (
        <div data-testid="roof-quickbar-error" className="mt-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 inline-flex items-center gap-1.5">
          <AlertCircle size={12} /> {err}
        </div>
      )}

      {/* ⚙ Fields modal — multi-select checklist of every Roof Details field. */}
      <Dialog open={fieldsOpen} onOpenChange={setFieldsOpen}>
        <DialogContent data-testid="roof-quickbar-fields-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Visible fields</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Tick the Roof Details fields that should stay visible on the builder bar. Your preference is remembered across appraisals.</p>
          <div className="grid grid-cols-2 gap-1.5 max-h-80 overflow-auto">
            {FIELD_CATALOGUE.map(f => {
              const checked = visibleFields.includes(f.key);
              return (
                <label key={f.key} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`roof-quickbar-fields-toggle-${f.key}`}
                    checked={checked}
                    onChange={(e) => {
                      setVisibleFields(cur => {
                        if (e.target.checked) return [...cur.filter(k => k !== f.key), f.key];
                        return cur.filter(k => k !== f.key);
                      });
                    }}
                    className="rounded text-[#D35400]"
                  />
                  <span className="text-slate-700">{f.label}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" data-testid="roof-quickbar-fields-reset" onClick={() => setVisibleFields(DEFAULT_VISIBLE)}>Reset to defaults</Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="roof-quickbar-manage-lists"
              onClick={() => { setFieldsOpen(false); window.open('/admin/roof-details-dropdowns', '_blank'); }}
            >Manage dropdown lists ↗</Button>
            <Button size="sm" data-testid="roof-quickbar-fields-close" onClick={() => setFieldsOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✎ Full edit modal — the existing RoofForm in a popup. Saves
          go through the same /roof-details/{id} endpoint, then we
          close the modal and refresh. */}
      <Dialog open={editFullOpen} onOpenChange={setEditFullOpen}>
        <DialogContent data-testid="roof-quickbar-edit-full-modal" className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Edit Roof Details — {selected.description || 'Roof'} ({selected.roof_type || '—'})</DialogTitle>
          </DialogHeader>
          {editRoofDraft && (
            <RoofForm
              roof={editRoofDraft}
              setRoof={setEditRoofDraft}
              onSave={async () => {
                setBusy(true);
                setErr(null);
                try {
                  const { _id, ...payload } = editRoofDraft;
                  await api.put(`/roof-details/${_id}`, payload);
                  // Cascade subtype + tile overrides onto the appraisal
                  // so the active builder sees the change on reload.
                  const inlineFields = [];
                  if (editRoofDraft.roof_type !== selected.roof_type || editRoofDraft.roof_brand !== selected.roof_brand) {
                    inlineFields.push(['roof_subtype', canonicaliseSubtype(editRoofDraft.roof_type, editRoofDraft.roof_brand)]);
                  }
                  if (editRoofDraft.roof_brand !== selected.roof_brand) {
                    inlineFields.push(['tile_brand_override', editRoofDraft.roof_brand || null]);
                  }
                  if (editRoofDraft.roof_profile !== selected.roof_profile) {
                    inlineFields.push(['tile_profile_override', editRoofDraft.roof_profile || null]);
                  }
                  for (const [f, v] of inlineFields) {
                    try { await api.patch(`/appraisals/${appraisal._id}/inline-field`, { field: f, value: v }); } catch (_) { /* non-fatal */ }
                  }
                  setEditFullOpen(false);
                  setPreviewByRoof(p => ({ ...p, [selectedId]: {} }));
                  if (onApplied) await onApplied();
                } catch (e) {
                  setErr(e?.response?.data?.detail || e.message || 'Save failed');
                } finally {
                  setBusy(false);
                }
              }}
              onCancel={() => setEditFullOpen(false)}
              saveLabel="Save & close"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * FieldChip — small pill that shows label + current value and opens
 * an inline editor on click. We deliberately render as a controlled
 * <select> / <input> inside the pill so the operator can change the
 * value with a single click + key rather than a popover dance.
 */
function FieldChip({ field, value, dirty, options, onChange }) {
  const v = value ?? '';
  const display = (v === '' || v === null || v === undefined) ? '—' : v;
  const dirtyCls = dirty
    ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300'
    : 'border-slate-300 bg-white hover:border-slate-400';

  return (
    <label
      data-testid={`roof-quickbar-field-${field.key}`}
      data-dirty={dirty ? '1' : '0'}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-all ${dirtyCls}`}
      title={`${field.label}${dirty ? ' · pending change (click Apply to save)' : ''}`}
    >
      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{field.label}</span>
      {field.kind === 'select' ? (
        <select
          data-testid={`roof-quickbar-input-${field.key}`}
          value={v}
          onChange={(e) => onChange(e.target.value)}
          className="px-1 py-0 text-xs border-0 bg-transparent focus:outline-none focus:ring-0 max-w-[10rem] font-semibold text-slate-800"
        >
          <option value="">—</option>
          {/* Render the current value as a legacy option if it's not
              in the canonical list — avoids the select looking blank
              for legacy short-forms (Mod Conc / Galvanb / etc.). */}
          {/* 2026-02 Go-Live · roof-colour redesign — seed carries
              indented brand names ("   Ebony"); we strip the leading
              whitespace on the stored value but keep it in the label
              so operators see brand names visually nested under their
              matching general colour. No-op on other dropdown types. */}
          {v && !options?.some(o => (typeof o === 'string' ? o.trim() : o) === v) && (
            <option value={v} className="text-amber-700 italic">{v} (legacy)</option>
          )}
          {(options || []).map(o => (
            <option key={o} value={typeof o === 'string' ? o.trim() : o}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          data-testid={`roof-quickbar-input-${field.key}`}
          type="number"
          step={field.step ?? 'any'}
          value={v}
          onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
          className="w-20 px-1 py-0 text-xs border-0 bg-transparent focus:outline-none focus:ring-0 text-right font-mono-num font-semibold text-slate-800"
        />
      )}
      {field.unit && field.kind === 'number' && (
        <span className="text-[9px] text-slate-400">{field.unit}</span>
      )}
      {dirty && <span className="text-[9px] text-amber-700 font-bold" title="Pending change">●</span>}
      {/* Read-only echo of the value when the field is too cluttered;
          kept off-screen but available for testid-based assertions. */}
      <span className="sr-only" data-testid={`roof-quickbar-display-${field.key}`}>{display}</span>
    </label>
  );
}
